import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListChecks,
  Quote,
  Link,
  Image,
  Table,
  Minus,
  FileCode,
  Save,
  FolderOpen,
  Download,
} from 'lucide-react'
import { useEditorStore } from '../../stores/editorStore'
import { useUIStore } from '../../stores/uiStore'
import { useFileSystem } from '../../hooks/useFileSystem'
import { cn } from '../../utils/dom'
import type { ViewMode } from '../../types'

export default function Toolbar() {
  const insertText = useEditorStore((s) => s.insertText)
  const wrapSelection = useEditorStore((s) => s.wrapSelection)
  const { saveCurrentFile, importFile, exportCurrentFile } = useFileSystem()
  const viewMode = useUIStore((s) => s.viewMode)
  const setViewMode = useUIStore((s) => s.setViewMode)

  const handleInsert = (text: string) => {
    insertText?.(text)
  }

  const handleWrap = (before: string, after: string) => {
    wrapSelection?.(before, after)
  }

  const actions = [
    { icon: <Heading1 size={16} />, label: '标题1', action: () => handleInsert('# ') },
    { icon: <Heading2 size={16} />, label: '标题2', action: () => handleInsert('## ') },
    { icon: <Heading3 size={16} />, label: '标题3', action: () => handleInsert('### ') },
    { icon: <Bold size={16} />, label: '粗体', action: () => handleWrap('**', '**') },
    { icon: <Italic size={16} />, label: '斜体', action: () => handleWrap('*', '*') },
    { icon: <Strikethrough size={16} />, label: '删除线', action: () => handleWrap('~~', '~~') },
    { icon: <Code size={16} />, label: '行内代码', action: () => handleWrap('`', '`') },
    { icon: <FileCode size={16} />, label: '代码块', action: () => handleInsert('\n```\n\n```\n') },
    { icon: <Quote size={16} />, label: '引用', action: () => handleInsert('> ') },
    { icon: <List size={16} />, label: '无序列表', action: () => handleInsert('- ') },
    { icon: <ListOrdered size={16} />, label: '有序列表', action: () => handleInsert('1. ') },
    { icon: <ListChecks size={16} />, label: '任务列表', action: () => handleInsert('- [ ] ') },
    { icon: <Link size={16} />, label: '链接', action: () => handleInsert('[text](url)') },
    { icon: <Image size={16} />, label: '图片', action: () => handleInsert('![alt](url)') },
    { icon: <Table size={16} />, label: '表格', action: () => handleInsert('\n| Header 1 | Header 2 | Header 3 |\n|----------|----------|----------|\n| Cell 1   | Cell 2   | Cell 3   |\n') },
    { icon: <Minus size={16} />, label: '分割线', action: () => handleInsert('\n---\n') },
    { icon: <Save size={16} />, label: '保存 (Ctrl+S)', action: saveCurrentFile },
    { icon: <FolderOpen size={16} />, label: '打开文件 (Ctrl+O)', action: importFile },
    { icon: <Download size={16} />, label: '导出 (Ctrl+E)', action: () => exportCurrentFile() },
  ]

  const viewModes: { mode: ViewMode; label: string }[] = [
    { mode: 'editor', label: '编辑' },
    { mode: 'split', label: '分屏' },
    { mode: 'preview', label: '预览' },
  ]

  return (
    <div className="flex items-center gap-0.5 px-2 py-1 border-b border-editor-border bg-editor-toolbar overflow-x-auto shrink-0">
      {actions.map((action, index) => (
        <button
          key={index}
          onClick={action.action}
          title={action.label}
          className="p-1.5 rounded hover:bg-editor-hover text-editor-fg/70 hover:text-editor-fg transition-colors"
        >
          {action.icon}
        </button>
      ))}

      <div className="ml-auto flex items-center gap-1 border-l border-editor-border pl-2">
        {viewModes.map(({ mode, label }) => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            className={cn(
              'px-2.5 py-1 text-xs rounded transition-colors',
              viewMode === mode
                ? 'bg-blue-500 text-white'
                : 'text-editor-fg/70 hover:bg-editor-hover'
            )}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  )
}
